import { Image, Link, StyleSheet, Text, View } from "@react-pdf/renderer";
import bwLogo from "../../../assets/img/p6-logo-black-and-white.png";
import DesignConstants from "../../../constants/DesignConstants";
import type { DownloadCodesClassMemberWithQR } from "../../../utils/qrCodeUtils";
import type { DownloadCodesPDFTranslations } from "./DownloadCodesPDFDocument";

interface DownloadCodesPDFCardProps {
    classMember: DownloadCodesClassMemberWithQR;
    schoolClassId: string;
    translations: DownloadCodesPDFTranslations;
    isLastColumn: boolean;
    isLastRow: boolean;
}

const QR_SIZE = "3cm";
const LOGO_WIDTH_CM = 2;
const LOGO_HEIGHT_CM = (LOGO_WIDTH_CM / 272) * 75; // maintain aspect ratio (272x75 original)

const DownloadCodesPDFCard = (props: DownloadCodesPDFCardProps) => {
    return (
        <View
            style={[
                styles.card,
                !props.isLastColumn ? styles.borderRight : {},
                !props.isLastRow ? styles.borderBottom : {},
            ]}
        >
            <View style={styles.qrContainer}>
                <Image
                    src={props.classMember.qrCodeDataUrl}
                    style={styles.qrImage}
                />
            </View>
            <View style={styles.infoContainer}>
                <View style={styles.logoRow}>
                    <View style={styles.logoContainer}>
                        <Image
                            src={bwLogo}
                            style={styles.logoImage}
                        />
                    </View>
                    <Text style={styles.klassenzimmerText}>{props.translations.classroom}</Text>
                </View>
                <Text style={styles.classMemberName}>{props.classMember.name}</Text>
                <Text style={styles.codeText}>
                    <Text style={styles.codeLabelBold}>{`${props.translations.classCode}: `}</Text>
                    {props.schoolClassId}
                </Text>
                <Text style={styles.codeText}>
                    <Text style={styles.codeLabelBold}>{`${props.translations.personalCode}: `}</Text>
                    {props.classMember.loginCode}
                </Text>
                <Link
                    src={props.classMember.classMemberUrl}
                    style={styles.urlLink}
                >
                    <Text style={styles.urlText}>{props.classMember.displayUrl}</Text>
                </Link>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    card: {
        flexDirection: "row",
        width: "50%",
        paddingHorizontal: DesignConstants.PDF.GAP_S,
        paddingVertical: DesignConstants.PDF.GAP_S,
        alignItems: "center",
    },
    borderRight: {
        borderRight: `2px dashed ${DesignConstants.PDF.GREY}`,
    },
    borderBottom: {
        borderBottom: `2px dashed ${DesignConstants.PDF.GREY}`,
    },
    qrContainer: {
        width: QR_SIZE,
        height: QR_SIZE,
        marginRight: DesignConstants.PDF.GAP_S,
    },
    qrImage: {
        width: "100%",
        height: "100%",
    },
    infoContainer: {
        flex: 1,
        flexDirection: "column",
        gap: DesignConstants.PDF.GAP_XXS,
    },
    logoRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: DesignConstants.PDF.GAP_XS,
        marginBottom: DesignConstants.PDF.GAP_XXS,
    },
    logoContainer: {
        width: LOGO_WIDTH_CM + "cm",
        height: LOGO_HEIGHT_CM + "cm",
    },
    logoImage: {
        width: "100%",
        height: "100%",
    },
    klassenzimmerText: {
        fontSize: DesignConstants.PDF.FONTSIZE_S,
        color: DesignConstants.COLORS.DARKEST_GREY,
    },
    classMemberName: {
        fontSize: DesignConstants.PDF.FONTSIZE_REGULAR,
        fontWeight: DesignConstants.PDF.SEMIBOLD,
        color: DesignConstants.COLORS.DARKEST_GREY,
    },
    codeText: {
        fontSize: DesignConstants.PDF.FONTSIZE_XS,
        color: DesignConstants.COLORS.DARK_GREY,
    },
    codeLabelBold: {
        fontWeight: DesignConstants.PDF.SEMIBOLD,
    },
    urlLink: {
        marginTop: DesignConstants.PDF.GAP_XXS,
        textDecoration: "none",
    },
    urlText: {
        fontSize: DesignConstants.PDF.FONTSIZE_XXS,
        color: DesignConstants.COLORS.MID_GREY,
    },
});

export default DownloadCodesPDFCard;
